import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Shield, Info, Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";

export default function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    smtp_host: "smtp.gmail.com", smtp_port: 587,
    smtp_user: "", smtp_password: "",
    from_email: "", from_name: "TileSphere",
    use_tls: true, enabled: true,
  });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/email-settings");
    setCfg(data);
    setForm((f) => ({
      ...f,
      smtp_host: data.smtp_host || f.smtp_host,
      smtp_port: data.smtp_port || 587,
      smtp_user: data.smtp_user || "",
      smtp_password: "",
      from_email: data.from_email || "",
      from_name: data.from_name || "TileSphere",
      use_tls: data.use_tls !== false,
      enabled: data.enabled !== false,
    }));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/email-settings", form);
      toast.success(data.ready ? "SMTP live — new approvals will send real emails" : "Saved (SMTP not yet ready)");
      setForm((f) => ({ ...f, smtp_password: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    if (!testTo) { toast.error("Enter an email address to test"); return; }
    setTesting(true);
    try {
      await api.post("/admin/email-settings/test", { to_email: testTo });
      toast.success(`Test message dispatched to ${testTo}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    finally { setTesting(false); }
  };

  const ready = cfg?.ready;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-email-settings">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Mail className="w-7 h-7" /> Email settings</h1>
        <p className="text-muted-foreground">Configure the SMTP server TileSphere uses to send welcome emails, invoices, password-resets and system notifications. Every message is rendered with the branded HTML template.</p>
      </div>

      <Card className="p-5 flex items-center justify-between border-2" data-testid="email-status">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Delivery status</div>
          <div className="mt-1 font-display text-2xl font-black flex items-center gap-2">
            {ready ? "SMTP live" : "Mock (no email dispatched)"}
            <Badge variant="outline" className={ready ? "border-emerald-400 bg-emerald-500/10 text-emerald-700" : "border-amber-400 bg-amber-500/10 text-amber-700"}>
              {ready ? "READY" : "SETUP NEEDED"}
            </Badge>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {ready
              ? `Host: ${cfg?.smtp_host}:${cfg?.smtp_port} · From: ${cfg?.from_email}`
              : "Fill in the SMTP details below and hit Save. The moment host + user + password + from_email are set, the system starts sending real emails."}
          </div>
        </div>
        {ready ? <CheckCircle2 className="w-12 h-12 text-emerald-500" /> : <AlertTriangle className="w-12 h-12 text-amber-500" />}
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2 font-display text-lg font-bold"><Shield className="w-5 h-5" /> SMTP credentials</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>SMTP host</Label>
            <Input placeholder="smtp.gmail.com" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="email-host" />
          </div>
          <div className="space-y-2">
            <Label>Port</Label>
            <Input type="number" placeholder="587" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value || "587") })} data-testid="email-port" />
          </div>
          <div className="space-y-2">
            <Label>Username</Label>
            <Input placeholder="you@yourdomain.com" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} data-testid="email-user" />
          </div>
          <div className="space-y-2">
            <Label>Password / App password</Label>
            <Input type="password" placeholder={cfg?.smtp_password_set ? "Saved · leave blank to keep" : "Paste SMTP password / Gmail App password"} value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} data-testid="email-password" />
          </div>
          <div className="space-y-2">
            <Label>From email</Label>
            <Input type="email" placeholder="hello@yourdomain.com" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="email-from" />
          </div>
          <div className="space-y-2">
            <Label>From name</Label>
            <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="email-from-name" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="font-semibold">Enabled</div><div className="text-xs text-muted-foreground">Master switch — turn off to force MOCK mode.</div></div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="email-enabled" />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="font-semibold">Use STARTTLS</div><div className="text-xs text-muted-foreground">Standard for port 587. Turn off for port 465 (implicit SSL).</div></div>
            <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v })} data-testid="email-tls" />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={busy} data-testid="email-save" className="h-11 px-6">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save & activate
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 font-display text-lg font-bold"><Send className="w-5 h-5" /> Send a test</div>
        <p className="text-sm text-muted-foreground">Sends the branded TileSphere welcome template to any inbox to verify SMTP works.</p>
        <div className="flex gap-2 flex-wrap">
          <Input type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="max-w-sm" data-testid="email-test-to" />
          <Button onClick={runTest} disabled={testing || !ready} data-testid="email-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send test
          </Button>
        </div>
        {!ready && <div className="text-[11px] text-amber-600">Save SMTP credentials first to enable the test send.</div>}
      </Card>

      <Card className="p-5 border-blue-500/30 bg-blue-500/[0.03]">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">Gmail App Password quick-start</div>
            <ol className="mt-2 space-y-1 text-muted-foreground list-decimal list-inside">
              <li>Enable 2-Step Verification on your Google Account.</li>
              <li>Visit <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-blue-500">Google App Passwords</a> and generate one for "Mail" → "Other (TileSphere)".</li>
              <li>Paste the 16-character app password above with Host <code className="text-xs">smtp.gmail.com</code>, Port <code className="text-xs">587</code>, TLS on.</li>
              <li>Hit Save, then Send test to your own inbox. Done — every approval now emails the branded template.</li>
            </ol>
          </div>
        </div>
      </Card>
    </div>
  );
}
